import { MPOST, POST, truncate } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { Leg } from 'types/Leg.ts';
import type { Shipment } from 'types/Shipment.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Select } from 'ui/control/Select.tsx';
import { useConfirm } from '#admin/component/DialogProvider.tsx';
import { getTranslatedError } from '#admin/helpers.ts';

type Props = {
  shipment: Shipment;
  leg: Leg;
  onSave?: (data: { supplier_id: string; leg_id: string }) => void;
  onClose: () => void;
  isOpen: boolean;
};

export const SupplierAssignmentDialog: React.FC<Props> = ({ shipment, leg, onSave, onClose, isOpen }) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const { data: suppliers, mutate: mutateSuppliers, isLoading } = useSWR<User[]>(['/user/list', { role: 'SUPPLIER', sort: [['name', 'asc']] }], POST);

  const validSuppliers = React.useMemo(() => {
    const validSuppliers = { none: t('options.none') };
    if (!suppliers) return validSuppliers;
    for (const supplier of suppliers) {
      const name = supplier.name.toUpperCase();
      const id = truncate(supplier.id, 8, '').toUpperCase();
      validSuppliers[supplier.id] = `${name} - ${id}`;
    }
    return validSuppliers;
  }, [suppliers, t]);

  const formMethods = useForm({
    mode: 'onTouched',
    criteriaMode: 'all',
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    shouldFocusError: true,
    defaultValues: {
      supplierId: leg.supplier_id || 'none',
    },
  });

  const { handleSubmit, formState } = formMethods;
  const { trigger: triggerAssign, isMutating: isMutatingAssign, error: errorAssign } = useSWRMutation(`/leg/${leg.id}/assign-supplier`, MPOST);
  const { trigger: triggerUnassign, isMutating: isMutatingUnassign, error: errorUnassign } = useSWRMutation(`/leg/${leg.id}/unassign-supplier`, MPOST);

  const isMutating = isMutatingAssign || isMutatingUnassign;
  const error = errorAssign || errorUnassign;

  const onSubmit = async ({ supplierId: selectedSupplierId }) => {
    if (!suppliers) return;
    const currentSupplier = suppliers.find((user) => user.id === leg.supplier_id);

    // NO CHANGE, DO NOTHING
    if (currentSupplier && currentSupplier.id === selectedSupplierId) {
      onClose();
      return;
    }

    // UNASSIGN
    if (selectedSupplierId === 'none' && (await confirm(t('dialogs.supplier.unassignConfirm')))) {
      await triggerUnassign({});
      void mutateSuppliers();
      return onSave?.({ supplier_id: 'none', leg_id: leg.id });
    }

    // ASSIGN
    if (!(await confirm(t('dialogs.supplier.assignConfirm')))) return;
    const targetSupplier = suppliers.find((user) => user.id === selectedSupplierId) as User;
    if (!targetSupplier) return;

    await triggerAssign({ supplier_id: selectedSupplierId });

    void mutateSuppliers();
    onSave?.({ supplier_id: selectedSupplierId, leg_id: leg.id });
  };

  if (isLoading) return <Spinner centered={true} />;

  return (
    <Modal key={leg.id} title={<h4>{t('dialogs.supplier.title')}</h4>} isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form className="flex w-[450px] flex-col gap-2.5" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-[100px_min-content] gap-2">
            <span>{t('labels.orderNumber')}:</span>
            <span>{shipment.logistics_ex_id}</span>
            <span>{t('labels.legId')}:</span>
            <span>{leg.logistics_ex_id}</span>
          </div>
          <div className="mt-6 flex flex-col gap-2">
            <Select name={'supplierId'} label={t('labels.supplierId')} options={validSuppliers} registerOptions={{ required: true }} />
          </div>

          <div className="mt-4 flex gap-5">
            <Button disabled={isMutating} className="blue-outlined" onClick={onClose}>
              {t('actions.back')}
            </Button>
            <Button
              type="submit"
              disabled={formState.isSubmitting || !formState.isValid || !formState.isDirty}
              loading={formState.isSubmitting}
              className="blue flex items-center justify-center"
            >
              {t('actions.save')}
            </Button>
          </div>
          {error && <span className="text-center text-red-500">{getTranslatedError(error, t) || t('dialogs.supplier.error')}</span>}
        </form>
      </FormProvider>
    </Modal>
  );
};
